<template>
  <v-container>
    <h1 class="my-6 text-h4 text-sm-h3">Developing group video themes</h1>

    <p>We provide tools to allow designers to create custom <router-link :to="{ name: 'group-video' }">group video</router-link> themes. Fundamentally, a group video theme consists of an interface that collects media from a group of people using a set of input elements with customizable prompts and constraints, and it contains logic to stitch the corresponding input media together into a themed video. These themes are complex &mdash; basic software development / JavaScript knowldege is required. See also our documentation for our <a href="/api-docs">web API</a>.</p>

    <faq-header anchor="getting-started" title="Getting started" class="mt-6 text-h5" />
    <ul>
      <li>Start developing a new group video theme from the <router-link :to="{ name: 'design', params: { tab: 'videos' } }">design portal</router-link> (registration required).</li>
      <li>Click on the "Create new" button.</li>
      <li>On the <v-icon>{{mdiInformation}}</v-icon> page, enter a user-facing <b>name</b> and <b>description</b> for your theme.</li>
      <li>Click on the <v-icon>{{mdiCodeJson}}</v-icon> button to edit the theme code. You'll probably want to use your own editor and simply paste you code there when you're redy to test. Make some updates and click "Save".</li>
      <li>Click on the <v-icon>{{mdiVideoVintage}}</v-icon> button to see how your theme looks to a project organizer.</li>
      <li>Drag media from our library of examples in the left column to test your theme. This is extra important, because we'll use the example you build as the user-facing preview of your theme on the <a href="/group-video" target="_blank">group video page</a>.</li>
      <li>Use the <v-icon>{{mdiFolderMultipleImage}}</v-icon> button to add / manage any static assets you want to incude directly in your theme.</li>
      <li>Use the <v-icon>{{mdiImport}}</v-icon> and <v-icon>{{mdiExport}}</v-icon> buttons to see the actual data that is passed to and from your theme's <code>build</code> function to help you debug.
      <li>When you're satisfied, submit your theme for our review by clicking the <v-icon>{{mdiFileUpload}}</v-icon> button.</li>
    </ul>


    <faq-header anchor="theme-modules" title="Theme modules" class="mt-6 text-h5" />
    <div>Group video themes are defined as classes in JavaScript modules. These classes must extend the <a href="#utils.AbstractProjectBuilder"><code>utils.AbstractProjectBuilder</code></a> class, which will give you access to the <a href="#project"><code>this.project</code></a> property along with a myriad of helper methods. You must implement two methods:</div>
    <ul>
      <li><code>get sections()</code> returns a list of the user-facing <a href="#input-sections"><b>input sections</b></a> based on the current state of <a href="#project"><code>this.project</code></a></li>
      <li><code>async build()</code> returns an object in the form of an <a href="#ellacard-video"><b>ellacard-video</b></a>, which we can play back live in a web browser and render to a video file</li>
    </ul>
    <div>Your <code>build</code> function can be asynchronous, but should still be fast. Make sure your class is reliable and optimized, as any problems / delays will create a bad user experience. Your class module must be entirely self-contained in a single file, and you may only <code>require</code> two external modules:</div>
    <ul>
      <li><a href="#assets-module"><code>assets</code></a> a function that allows you to use static assets you've uploaded via the <v-icon>{{mdiFolderMultipleImage}}</v-icon> page</li>
      <li><a href="#utils-module"><code>utils</code></a> contains various helper classes and functions</li>
    </ul>

    <div class="mt-4">You can open any of our published themes as an example:</div>
    <div class="d-flex ma-n2">
      <v-select
        v-select-in-line
        class="ma-2 d-inline-block flex-grow-0"
        hide-details dense
        :loading="loadExamplesStatus.pending"
        :items="exampleTemplates"
        v-model="selectedExampleTemplateId"
      />
      <v-btn class="ma-2" small :disabled="!selectedExampleTemplateId" @click="$router.push({ name: 'design-video-template', params: { templateId: selectedExampleTemplateId } })">Open<v-icon class="ml-2" small>{{mdiOpenInNew}}</v-icon></v-btn>
    </div>

    <div class="mt-4">See a well-documented example theme module <a href="#annotated-example">here</a>. Create a new theme in the <router-link :to="{ name: 'design', params: { tab: 'videos' } }">design portal</router-link> and click on the <v-icon>{{mdiCodeJson}}</v-icon> button to see an annotated default skeleton. Here's a quick idea of how your module class should be structured:</div>
<pre class="code">
const utils = require('utils');

class Builder extends utils.AbstractProjectBuilder {

  get sections() {
    return [ &lt;input section definitions here&gt; ];
  }

  build() {
    &lt;logic here&gt;
    return this.buildResources();
  }
}

module.exports = Builder;
</pre>


    <faq-header anchor="project" title="Project" class="mt-6 text-h5" />
    <div>The <a href="#input-sections"><b>input sections</b></a> defined by your module's <code>get sections()</code> method will be used to generate the user interface to collect content from users. Read more about how to define those sections <a href="#input-sections">here</a>. Each of those resulting inputs will be delivered as part of the <b>project</b> that will be available to you as the <code>this.project</code> property in your builder. You could assemble your output media resources based on that raw input, but the abstract base class provides helper methods that automatically convert valid user input into media resources. Read more about them <a href="#utils.AbstractProjectBuilder.getResources">here</a>. Nonetheless, the <b>project</b> argument is structured as follows:</div>
<pre class="code">
{
  created_at: ISO datetime str,
  user_id: UUID str,
  sections: {
    // These sections contain the user input and correspond to the section 'id' keys you defined in your module's 'sections'
    'section-id-1': {
      inputs: [
        {
          created_at: ISO datetime str,
          updated_at: ISO datetime str,
          input_id: UUID str, // A unique ID for this input
          user_id: UUID str, // The unique user ID associated with this input
          version_id: int, // Successive updates to this input will increment the version number
          type: str, // The input type (note that differs from media types)
          data: { ... } // The 'data' depends on the type of this input
        },
        ...
      ]
    },
    ...
  }
}
</pre>


    <faq-header anchor="input-sections" title="Input sections" class="mt-6 text-h5" />
    <p>This section lists the input types available to use when you set up the <b>input sections</b> in your theme via the <code>get sections()</code> method. The <i>input spec</i> tab shows the options you have to configure the user input. The <i>project input data</i> tab shows the <code>data</code> property from the output of <a href="#utils.AbstractProjectBuilder.getInputs"><code>this.getInputs(…)</code></a> for the corresponding input type. The <i>builder resources</i> tab shows the output of <a href="#utils.AbstractProjectBuilder.getResources"><code>this.getResources(…)</code></a> for the corresponding input type &mdash; this is generally how you should interact with inputs as they are already packaged as media resources for you (sans automations).</p>
    <div>The base input section objects you return in your list from the <code>get sections()</code> method should be structured as follows, where the 'spec' property refers to the <i>input spec</i>.</div>
<pre class="code">
{
  // The section ID must be unique
  id: str,

  // Whether or not we should show this section to only the project organizer
  owner_only: bool,

  // Whether or not this input is required.
  required: false,

  title: str, // Optional user-facing text, each input type has a default title (like "Add a picture" for IMG)
  subtitle: str, // Optional user-facing text, provides more context than just the title itself

  // The user-facing description, or prompt, that tells a user what to do for this section
  details: str,

  type: str, // Must be a valid input type

  // The input specification whose content will vary based on the type
  spec: ...
}
</pre>

    <faq-header anchor="input-sections.AUDIO_CHOICE" title="AUDIO_CHOICE" class="mt-6" />
    <div>Allows the user to pick from a list of audio files that you specify with the ability to preview the files. You can allow users to select "None" if you provide an option with no <code>src</code>. You can also set the <code>default</code> property for one of your options. This simply adds a checkmark in the user interface next to that option &mdash; it's still up to you to program your theme to add the appropriate default audio resource.</div>
    <input-section-tabs v-model="tab_AUDIO_CHOICE" />
    <v-tabs-items v-model="tab_AUDIO_CHOICE">
      <v-tab-item>
<pre class="code">
{
  options: [
    {
      name: str,
      src: null | assets('audio_file.mp3'),

      // Optional, only one option should be set as default
      default: true
    },
    ...
  ]
}
</pre>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
{
  src: str,
  duration: float // In seconds
}
</pre>
      </v-tab-item>
      <v-tab-item>
        Yields a single AUDIO resource:
<pre class="code">
{
  id: str,
  type: 'AUDIO',
  src: str,
  meta: {
    duration: float // In seconds
  }
}
</pre>
      </v-tab-item>
    </v-tabs-items>

    <faq-header anchor="input-sections.IMG" title="IMG" class="mt-6" />
    <div>Allows the user to upload a single image and control its size / rotation. By default, a user can crop an image to any size with no letterboxing. If you specify a 'fixed_aspect_ratio', we will automatically add a letterbox with equal black bars (either vertically or horizontally as needed). If there is no 'fixed_aspect_ratio', you can set a 'preferred_aspect_ratio' which simply governs the size of the input presented to users, though nothing is enforced.</div>
    <input-section-tabs v-model="tab_IMG" />
    <v-tabs-items v-model="tab_IMG">
      <v-tab-item>
<pre class="code">
{
  fixed_aspect_ratio: float, // Optional, the ratio of width / height
  preferred_aspect_ratio: float // Optional, defaults to 4x3
}
</pre>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
{
  src: str,
  crop: null | { top: float, right: float, bottom: float, left: float },
  r: 0 | 90 | 180 | 270,
  letterbox: null | float, // Will match the 'fixed_aspect_ratio' if one was specified

  // The dimensions of the pre-cropped, pre-rotated image
  size: { w: int, h: int }
}
</pre>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
{
  id: str,
  type: 'IMG',
  src: str,
  crop: null | { left: float, top: float, right: float, bottom: float },
  r: 0 | 90 | 180 | 270,
  letterbox: null | float,
  meta: {
    // The actual dimensions of the cropped, rotated image
    size: { w: int, h: int }
  }
},
...
</pre>
      </v-tab-item>
    </v-tabs-items>

    <faq-header anchor="input-sections.IMG_GROUP" title="IMG_GROUP" class="mt-6" />
    <div>Allows the user to upload a group of images at once and control their order. The user can rotate / resize each individual image. By default, a user can crop images to any size with no letterboxing. If you specify a 'fixed_aspect_ratio', we will automatically letterbox the images with equal black bars (either vertically or horizontally as needed). If there is no 'fixed_aspect_ratio', you can set a 'preferred_aspect_ratio' which simply governs the size of the input presented to users, though nothing is enforced.</div>
    <input-section-tabs v-model="tab_IMG_GROUP" />
    <v-tabs-items v-model="tab_IMG_GROUP">
      <v-tab-item>
<pre class="code">
{
  min: int, // Optional, note that you will never get passed data for an IMG_GROUP input with zero images
  max: int,
  fixed_aspect_ratio: float, // Optional, the ratio of width / height
  preferred_aspect_ratio: float // Optional, defaults to 4x3
}
</pre>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
{
  letterbox: null | float, // Will match the 'fixed_aspect_ratio' if one was specified
  imgs: [
    {
      src: str,

      // The dimensions of the pre-cropped, pre-rotated source image
      size: { w: int, h: int },

      crop: null | { left: float, top: float, right: float, bottom: float },
      r: 0 | 90 | 180 | 270
    },
    ...
  ]
}
</pre>
      </v-tab-item>
      <v-tab-item>
        Yields multiple IMG resources in the specific order set by the user
<pre class="code">
{
  id: str,
  type: 'IMG',
  src: str,
  crop: null | { top: float, right: float, bottom: float, left: float },
  r: 0 | 90 | 180 | 270,
  letterbox: null | float,
  meta: {
    // The actual dimensions of the cropped, rotated image
    size: { w: int, h: int }
  }
},
...
</pre>
      </v-tab-item>
    </v-tabs-items>

    <faq-header anchor="input-sections.MULTIPLE_CHOICE" title="MULTIPLE_CHOICE" class="mt-6" />
    <div>Allows the user to select from a list of text options. Can also be configured to allow multiple selections and to allow users to write in options (multiple allowed). Delivers its results as an array of 'custom' options that the user wrote in, and the 'selected' options as strings.</div>
    <input-section-tabs v-model="tab_MULTIPLE_CHOICE" />
    <v-tabs-items v-model="tab_MULTIPLE_CHOICE">
      <v-tab-item>
<pre class="code">
{
  // Optional, if false-ish, the user can only select one option
  // If set to true, the user can select any number of options
  // If an object, the user must select a number of options between the 'min' and 'max' (inclusive). Note that only one of 'min' and 'max' needs to be provided, though both will be respected
  multiple: null | true | { min: int, max: int },

  // Optional, the number of options the user is allowed to write-in (true == 1), defaults to 0
  custom: int,

  // The pre-populated options in the list
  options: [
    {
      text: str,
      default: true // Optional, set to true to have this option be initially checked
    },
    ...
  ]
}
</pre>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
{
  // The text of the user-selected options in the order in which they were selected
  selected: [ str, ... ],

  // The text of any custom options the user wrote in
  custom: [ str, ... ]
}
</pre>
      </v-tab-item>
      <v-tab-item>
        None
      </v-tab-item>
    </v-tabs-items>

    <faq-header anchor="input-sectionsTEXT" title="TEXT" class="mt-6" />
    <div>Allows the user to enter custom text, optionally with the ability to change the font, style, and color. Text the user enters will automatically resize to fit the specified <code>size</code> &mdash; the <code>font_size</code> will shrink as small as the <code>min_font_size</code>. You can make any of the explicit input properties configurable, including the <code>text</code> itself, which allows you to use one global text input to govern the styles of all other text inputs.</div>
    <input-section-tabs v-model="tab_TEXT" />
    <v-tabs-items v-model="tab_TEXT">
      <v-tab-item>
<pre class="code">
{
  // The size of the output image to which the text will be rendered
  size: { w: int, h: int },

  // Text can automatically resize to its container. It will start out at the 'font_size' specified, and as the user enters more text that would overflow the container, the font size will automatically shrink as far as the 'min_font_size' value allows it
  font_size: int,
  min_font_size: int,

  // These default values are always required
  text: str, // Placeholder text (or example text if 'text' is not a configurable option)
  font: 'Nunito', // Font family (must be supported - see below for a list of supported fonts)
  color: '#FEFEFE', // CSS color string
  bold: bool,
  italic: bool,
  underline: bool,

  // This property determines how much a user can configure. If this object is empty, the user won't be able to change anything other than the text itself.
  options: {
    // A user will only be able to edit the default text if you set this property to true
    text: bool,

    // Set to true to show all supported font choices (see list below), or you can explicitly list the fonts you want to let the user choose from - make sure the default 'font' you specified earlier is in this list
    font: bool | array,

    // Set to true to show the default colors as used in the cards (see list below), or you can explicitly list the colors to let the user chosoe from - make sure your default 'color' is in this list
    color: bool | array,

    // Set to true to allow the user to configure these options
    bold: bool,
    italic: bool,
    underline: bool
  }
}
</pre>
        <v-expansion-panels class="mb-n4 justify-start">
          <v-row>
            <v-col cols="12" sm="6">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <faq-header anchor="default-fonts" title="Default fonts" class="overline" />
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ul v-for="font in fonts">
                    <li>
                      <span v-bind:style="{ 'font-family': font }">{{font}}</span>
                    </li>
                  </ul>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-col>
            <v-col cols="12" sm="6">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <faq-header anchor="default-colors" title="Default colors" class="overline" />
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ul v-for="color in colors">
                    <li>
                      <span v-bind:style="{ color, 'background-color': getBackground(color) }">{{color}}</span>
                    </li>
                  </ul>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-col>
          </v-row>
        </v-expansion-panels>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
{
  // This object will contain only the properties that the user explicitly set. If not present, then we defer to the defaults in the section definition.
  text: str,
  font: font family str,
  color: CSS color str,
  bold: bool,
  italic: bool,
  underline: bool
}
</pre>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
{
  id: str,
  type: 'TEXT',

  // The text will initially be rendered as an image of this size. This avoids dynamic font resizing should your automations scale it up or down. As such, make sure the size you declare in the input matches the size of your largest automation to avoid blurriness.
  size: { w: int, h: int },
  
  font_size: int,
  min_font_size: int,

  text: str,
  font: font family str,
  color: CSS color str,
  bold: bool,
  italic: bool,
  underline: bool,

  meta: {
  }
}
</pre>
      </v-tab-item>
    </v-tabs-items>

    <faq-header anchor="input-sections.VIDEO" title="VIDEO" class="mt-6" />
    <div>Allows the user to upload / record a video. The user also has the ability to trim, resize, and rotate the video by 90º increments. By default, a user can crop a video to any size with no letterboxing. If you specify a 'fixed_aspect_ratio', we will automatically letterbox the video with equal black bars (either vertically or horizontally as needed). If there is no 'fixed_aspect_ratio', you can set a 'preferred_aspect_ratio' which simply governs the size of the input presented to users, though nothing is enforced.</div>
    <input-section-tabs v-model="tab_VIDEO" />
    <v-tabs-items v-model="tab_VIDEO">
      <v-tab-item>
<pre class="code">
{
  fixed_aspect_ratio: float, // Optional, the ratio of width / height
  preferred_aspect_ratio: float, // Optional, defaults to 16x9
  duration: {
    min_s: int, // Optional (defaults to 0)
    max_s: int // This will also govern the max data size of the input
  },

  // Set to true to present an interface that mutes the uploaded video
  exclude_audio: bool // Optional (defualts to false)
}
</pre>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
data: {
  src: str,

  // The dimensions of the pre-cropped, pre-rotated video
  size: { w: int, h: int },

  // The actual duration of the pre-trimmed video file
  duration: float,

  // True only if the input video has an audio track
  has_audio: bool,

  // The intrinsic volume of the video as a percentage of baseline, should be in the range [ 0.05, 2 ]
  v: float,

  // The intrinsic rotation of the video
  r: 0 | 90 | 180 | 270,

  // The percentage margins to crop the video from each side. For example, to crop the 400x300 upper left quadrant of an 800x600 image, specify the following: { top: 0, right: .5, bottom: .5, left: 0 }. Negative margins will be explicitly filled in with black, so a letterboxed 9x16 vertical video in a 16x9 widescreen layout would use the following: { top: float, right: -1.0802, bottom: float, left: -1.0802 }. Note that the 'w' and 'h' specified in the automations will be applied to the cropped area.
  crop: null | { top: float, left: float, right: float, bottom: float },

  // Will match the 'fixed_aspect_ratio' if one was specified
  letterbox: null | float,

  // Optional, the absolute trimmed timestamps of the video, note that [ 0, &lt;duration&gt; ] is perfectly valid
  trim: null | [ float, float ]
}
</pre>
      </v-tab-item>
      <v-tab-item>
<pre class="code">
{
  id: str,

  // The type depends on whether or not the linked video has an audio track
  type: 'AUDIO_VIDEO' | VIDEO',

  src: str,

  // Intrinsic clip adjustments (see project input data tab for more details) - these will be applied in conjunction with the values specified by any automations
  v: null | float,
  r: null | 0 | 90 | 180 | 270,
  crop: null | { top: float, right: float, bottom: float, left: float },

  letterbox: null | float,

  // Where to start playback of the video (if it has been trimmed)
  t_offset: null | int,

  meta: {
    // The actual dimensions of the cropped, rotated video
    size: { w: int, h: int },

    // The duration in seconds of the trimmed video
    duration: float
  }
}
</pre>
      </v-tab-item>
    </v-tabs-items>

    <h3 class="mt-6">Additional input types</h3>
    <p>Do you need an additional input type to really make your theme work? Please <a href="mailto:design@ellacard.com">let us know</a> and explain your use case! We're developing more all the time. Note that theoretical theme inputs don't necessarily have to be related to media. For example, a user could be prompted to answer a question or vote on something, and your theme could use that information to help construct the video.</p>


    <faq-header anchor="ellacard-video" title="Ellacard-video" class="mt-6 text-h5" />
    <div>This section describes the <b>ellacard-video</b> format &mdash; the output of your theme's <code>build()</code> function. You should generally use <a href="#utils.AbstractProjectBuilder.buildResources"><code>this.buildResources()</code></a> function to generate this structure, but it's explained here to help you understand exactly what's going on. This structure describes how various media should be included in the video. A media element can be a video (with or without an audio track), still image, text (with style), or audio file. Each media element (except for text) should specify it's <code>src</code> URL, which allows us to render a live preview of your video over the internet. Importantly, every media element should include at least two <i>automations</i>. Automations allow you make your media dynamic, such as panning across an image or video, or gradually adjusting the volume of a background audio track. An automation entry describes the state of your media at a given timestamp <code>t</code>, and we'll automatically linearly interpolate between your automation entries.</div>
<pre class="code">
{
  // Specify your coordinate system for laying out content. We may render the actual video itself at different scales.
  // Warning: some of the helper functions assume that the size will be set to 1920x1080.
  w: 1920,
  h: 1080,
  media: [
    {
      // A unique ID for this media element across the whole JSON structure
      id: str,

      // The type of this media component
      type: AUDIO | AUDIO_VIDEO | IMG | TEXT | VIDEO,

      // The URL for the native (highest quality) source of this media. Only relevant if not TEXT.
      [src]: str,

      // The following properties apply only to TEXT media elements. When the resource is initially loaded, we use these properties to render an image. From that point on, TEXT is treated like an IMG.
      [size]: { w: int, h: int },
      [font_size]: int,
      [min_font_size]: int,
      [text]: str,
      [font]: Font family str,
      [color]: CSS color str,
      [bold]: bool,
      [italic]: bool,
      [underline]: bool,

      // The offset from the start of the media to begin automations (optional), does not apply to images or text
      t_offset: null | float,

      // Duration in seconds to transition opacity (for visual media) or volume (for audio) at the start / end of the automation (optional)
      fade_in: null | float,
      fade_out: null | float,

      // The intrinsic volume adjustment of an AUDIO or AUDIO_VIDEO element. It will be multiplied by the volume derived from the automations. This value should be in the range [ 0.05, 2 ].
      v: null | float,

      // For AUDIO_VIDEO, VIDEO, and IMG, these properties describe the intrinsic rotation and percentage to crop off from each edge - crop is applied to the native video size, not the size laid out in the automation (optional)
      r: null | 0 | 90 | 180 | 270,
      crop: null | { top: float, left: float, right: float, bottom: float },
      letterbox: null | float,

      // All the properties in the automations will be interpolated between timestamps
      automation: [
        {
          // The absolute timestamp (in seconds) for when this media element begins playback
          t: float,

          // The center coordinates of the media element (for visual media elements only: AUDIO_VIDEO | VIDEO | IMG)
          x: float,
          y: float,

          // The unrotated size of the media element (for visual media elements only)
          w: float,
          h: float,

          // The rotation of the media element (in degrees)
          r: float,

          // For media elements with audio: AUDIO_VIDEO | AUDIO, a value in the range [0, 1] that specifies a percentage of baseline volume - fade in / out will be applied over the top of this value
          v: float
        },

        // Must have at least two automation entries in order of increasing 't'
        ...
      ]
    },
    ...
  ]
}
</pre>


    <faq-header anchor="helper-modules" title="Helper modules" class="mt-6 text-h5" />
    <div>Your theme module must be entirely self-contained within one file, and it can't depend on any external modules, with the exception of the <a href="#assets-module"><code>assets</code></a> and <a href="#utils-module"><code>utils</code></a> modules that we provide.</div>


    <faq-header anchor="assets-module" title="Assets module" class="mt-6 text-h5" />
    <div>The <code>assets</code> module is itself a function that allows you reference resources you've uploaded in the <v-icon>{{mdiFolderMultipleImage}}</v-icon> page by the ID you've assigned them there.</div>

    <faq-header anchor="assets" title="function assets(assetId)" class="mt-6" />
    <div class="ml-4">
      Returns the URL of the associated asset.
      <ul>
        <li>assetId: the ID of the asset you want to use as specified in the assets page</li>
      </ul>
    </div>


    <faq-header anchor="utils-module" title="Utils module" class="mt-6 text-h5" />
    <div>The <code>utils</code> module provides the following functions / classes to help you build themes and apply standard effects.</div>

    <faq-header anchor="utils.buildFrame" title="function utils.buildFrame(resource, thickness, color, [size])" class="mt-6" />
    <div class="ml-4">
      Builds a frame resource around the given visual media resource such that the frame will slightly overlap the edges of the visual media. The frame's matadata 'layer' property will be automatically set to slightly greater than the associated resource. Returns an IMG resource that can be added to the builder using <a href="#utils.AbstractProjectBuilder.addResource"><code>this.addResource(…)</code></a>.
      <ul>
        <li>resource: the theme builder resource to frame, must be visual (AUDIO_VIDEO, VIDEO, or IMG) and have all of its automations in place</li>
        <li>thickness: the thickness of the frame (in pixels)</li>
        <li>color: an RGB array of three ints in the range [0,255]</li>
        <li>[size]: object with the following properties (optional, defaults to the size of the largest automation)
          <ul>
            <li>w: int width of the frame</li>
            <li>h: int height of the frame</li>
          </ul>
        </li>
      </ul>
    </div>

    <faq-header anchor="utils.buildSlideshowKenBurns" title="function utils.buildSlideshowKenBurns(resource, t0, [options])" class="mt-6" />
    <div class="ml-4">
      Attaches automations to the given IMG resources to make a slideshow using the ken-burns effect. Images that are not exactly 16x9 will have have some letterboxing that shows the background. Returns the total duration of the automations.
      <ul>
        <li>resources: the IMG resources to put into the slideshow, should have the 'meta' property set containing at
    least 'w' and 'h'</li>
        <li>t0: the initial timestamp from which to begin the automations</li>
        <li>[options]: object with the following optional properties (optional)
          <ul>
            <li>[duration=5]: the amount of time each image is shown for. Note that there will be some overlap depending on if 'crossfade' is set.</li>
            <li>[crossfade=0]: time in seconds to cross fade between images. Will effectively subtract from the specified 'duration' as images will need to overlap. Also sets the 'fade_out' property on all but the final resource.</li>
            <li>[motionX=40]: amount in pixels by which to pan images horizontally</li>
            <li>[motionY=20]: amount in pixels by which to pan images vertically</li>
            <li>[zoom=1.08]: the zoome level at which to start (always ends at 1)</li>
          </ul>
        </li>
      </ul>
    </div>

    <faq-header anchor="utils.layout.getScale" title="function utils.layout.getScale(w, h, fitW, fitH, mode)" class="mt-6" />
    <div class="ml-4">Returns the scale needed to fit the specified 'w' and 'h' into the size defined by 'fitW' and 'fitW', abiding by the mode of either 'contain' or 'cover'. The modes have the same meaning as for the object-fit property in CSS, see <a href="https://www.w3schools.com/css/css3_object-fit.asp" target="_blank">here</a>.</div>

    <!-- <faq-header anchor="utils.layout.getRotatedSize" title="function utils.layout.getRotatedSize(size, r)" class="mt-6" />
    <div class="ml-4">Returns an object { w, h } that defines the size for an input with the specified 'size' and rotation 'r' in degrees</div> -->

    <faq-header anchor="utils.layout.scaleForArea" title="function utils.layout.scaleForArea(size, area)" class="mt-6" />
    <div class="ml-4">Returns an object { w, h } adjusted so that it has the same aspect ratio as the input size, but its area is as given</div>

    <faq-header anchor="utils.spreadHorizontally" title="function utils.spreadHorizontally(resources, t0, [options])" class="mt-6" />
    <div class="ml-4">
      Attaches automations to the given video resources to result in a random-ish zig-zag effect as seen during the smile-and-wave section of the holiday cheers theme. If there are more than 'maxGroupSize' items, the items will be broken up into eqaully sized groups (as much as possible) and the groups will be shown sequentially. Returns the total duration of the automations.
      <ul>
        <li>resources: the AUDIO_VIDEO or VIDEO resources to lay out, should have the 'meta' property set containing 'w', 'h', 'r', and 'duration'</li>
        <li>t0: the initial timestamp from which to begin the automations</li>
        <li>[options]: object with the following optional properties (optional)
          <ul>
            <li>[maxGroupSize=4]: the max number of videos to show at once</li>
            <li>[top=40]: the top margin in pixels</li>
            <li>[right=40]: the right margin in pixels</li>
            <li>[bottom=40]: the bottom margin in pixels</li>
            <li>[left=40]: the left margin in pixels</li>
            <li>[centerYOffsetPct=0.1]: the percentage of height after applying the margins to which these videos should be offset from the center horizontal axis when zig-zagging. Set to 0 for a straight line. Set to .5 to maximize zig-zag effect.</li>
            <li>[rotationOffset=15]: the angle in degrees to stagger each item</li>
            <li>[delay=0.25]: the delay between adding successive items (in a single group)</li>
          </ul>
        </li>
      </ul>
    </div>

    <faq-header anchor="utils.AbstractProjectBuilder" title="class utils.AbstractProjectBuilder" class="mt-6" />
    <div class="ml-4">
      This is the principal tool you will use to build themes. Your <a href="#theme-modules">theme module</a> must extend this class. Your subclass will be instantiated each time the project is built. This class's constructor assigns the raw <a href="#project"><b>project</b></a> to the <code>this.project</code> property, and it automatically validates and converts the raw inputs into media resources based on the type of input. Note that input section types ≠ media resource types! For example, a VIDEO input happens to yield a similarly-named VIDEO resource, but an AUDIO_CHOICE input may yield an AUDIO resource, or nothing. An IMG_GROUP input type will yield multiple IMG resources. Read more about the types of <a href="#input-sections"><b>input sections</b></a> to see how input types you specify will be automatically made available to you as resource types.
    </div>

    <faq-header anchor="utils.AbstractProjectBuilder.getInputs" title="utils.AbstractProjectBuilder.getInputs(sectionId=null)" class="mt-6" />
    <div class="ml-4">
      Provides access to the raw input data for the specified section. This is useful if you want to read user input from a section that does not directly create a media type.
      <ul>
        <li>[sectionId]: the section 'id' specified by your <code>get sections()</code> implementation. Optional, by default will fetch all the inputs for all sections.</li>
      </ul>
    </div>

    <faq-header anchor="utils.AbstractProjectBuilder.getInput" title="utils.AbstractProjectBuilder.getInput(sectionId)" class="mt-6" />
    <div class="ml-4">
      Same as getInputs, but for sections where there is at most one resource (sections marked with 'owner_only' that yield a single output). Returns the output or null if one is not found.
      <ul>
        <li>sectionId: the section 'id' specified by your <code>get sections()</code> implementation</li>
      </ul>
    </div>

    <faq-header anchor="utils.AbstractProjectBuilder.getResources" title="utils.AbstractProjectBuilder.getResources(sectionId=null)" class="mt-6" />
    <div class="ml-4">
      Returns all the <i>valid</i> media resources as derived from the specified section in the order set by the organizer / users. Invalid resources (such as a video that is too long for the specified duration) should have accompanying messaging in the UI that informs the user that their input will not be included in the project. The media resources returned by this method will not have automations attached, so will not be included in the output of the call to 'build' until you attach some.
      <ul>
        <li>[sectionId]: the section 'id' specified by your <code>get sections()</code> implementation. Optional, by default will fetch all the resources for all sections.</li>
      </ul>
    </div>

    <faq-header anchor="utils.AbstractProjectBuilder.getResource" title="utils.AbstractProjectBuilder.getResource(sectionId)" class="mt-6" />
    <div class="ml-4">
      Same as getResources, but for sections where there is at most one resource (sections marked with 'owner_only' that yield a single output). Returns the output or null if one is not found.
      <ul>
        <li>sectionId: the section 'id' specified by your <code>get sections()</code> implementation</li>
      </ul>
    </div>

    <faq-header anchor="utils.AbstractProjectBuilder.addResource" title="utils.AbstractProjectBuilder.addResource(resource)" class="mt-6" />
    <div class="ml-4">
      Adds a media resource to the builder
      <ul>
        <li>resource: the media resource to add to the builder</li>
      </ul>
    </div>

    <faq-header anchor="utils.AbstractProjectBuilder.buildResources" title="utils.AbstractProjectBuilder.buildResources()" class="mt-6" />
    <div class="ml-4">
      Builds all the media resources that were automatically created on initialization or added using 'addResource' that have valid automations. Resources will be sorted according to the 'meta.layer' property. Any resources that have 'meta.duration' set will automatically loop if their automations extend beyond that duration. Returns an <a href="#ellacard-video"><b>ellacard-video</b></a> structure.
    </div>

    <faq-header anchor="utils.AbstractProjectBuilder.randomInt" title="utils.AbstractProjectBuilder.randomInt(min, max)" class="mt-6" />
    <div class="ml-4">
      Generates a consistent pseudo-random number in the specified range. The seed is unique to a project (based on when it was created) to ensure consistency within a project.
      <ul>
        <li>min: the lowest range limit (inclusive)</li>
        <li>max: the upper range limit (exclusive)</li>
      </ul>
    </div>


    <faq-header anchor="annotated-example" title="Annotated example" class="mt-6 text-h5" />
<pre class="code">
const assets = require('assets');
const { AbstractProjectBuilder } = require('utils');

// For this theme, the sections are not dynamic - they do not change based on project input, so we can declare them statically here.
const SECTIONS = [
  {
    id: 'title',
    owner_only: true,
    required: false,
    subtitle: 'Title',
    details: 'Enter a title for your video',
    type: 'TEXT',
    spec: {
      size: { w: 1840, h: 460 },
      font_size: 164,
      min_font_size: 96,
      text: 'Name',
      font: 'Luckiest Guy',
      color: '#FFF',
      bold: true,
      italic: false,
      underline: false,
      options: {
        // We only let the user configure these options
        text: true,
        font: true
      }
    }
  },
  {
    id: 'say-hello',
    owner_only: false,
    required: false,
    type: 'VIDEO',
    subtitle: 'Say hello',
    details: `Add a video of you waving and saying hello!`,
    spec: {
      duration: {
        max_s: 10
      }
    }
  }
];

class Builder extends AbstractProjectBuilder {

  get sections() {
    // Note that the 'project' could be null at this point
    return SECTIONS;
  }

  build() {
    let t = 0;

    // We declared an input section called 'title' that was for the organizer only. When a section has 0 or 1 resources, you can access it using 'getResource' so you can set automations for it
    const titleResource = this.getResource('title');
    titleResource.automation = [
      // The automations are the key to the ellacard video theme. Every media resource in the output file must have at least two automations, though you can set as many as you want. Video renderers will linerly interpolate all the properties between the timestamps in your automations, including volume. For visual media (IMG, TEXT, and VIDEO), you must specify the 'x' and 'y' coordinates of the center of the object, its size as 'w' and 'h', and its rotation 'r' in degrees. For VIDEO and AUDIO, you must specify the 'v' property for volume in the range [0,1].
      { t: 0, x: 960, y: 540, w: 1920, h: 1080, r: 0 },
      { t: 5, x: 960, y: 540, w: 1920, h: 1080, r: 0 }
    ]

    // The section called 'say-hello' open to all contributors. When a section can have more than 1 resource, use 'getResources'
    t += 5;
    for (const resource of this.getResources('say-hello')) {
      resource.automation = [
        { t, x: 960, y: 540, w: 1920, h: 1080, r: 0, v: 1 },
        { t: t + resource.meta.duration, x: 960, y: 540, w: 1920, h: 1080, r: 0, v: 1 }
      ];
      t += 5;
    }

    // You can also add your own resources directly to the builder, like if you had a static background asset to include that you wanted to show throughout the entire video. Note that we provide a means to include assets using our CDN via the 'assets' tab in the design video theme tool. Do not use external URLs.
    background = {
      id: 'background',
      type: 'IMG',
      // Assuming you've uploaded the corresponding asset
      src: assets('background.jpg'),
      meta: {
        // Metadata is any information that doesn't directly get used when rendering the video. The builder sets and uses certain metadata when it creates resources, like the 'size' property for images or videos, or the 'duration' property for audio and video resources. In the case of 'duration', the builder uses that property to determine if it has to loop the resource in order to keep it playing through all the automations that have been attached. Without that 'meta' property, the builder will not loop resources. Various layout / helper functions rely on these properties as well, so be careful that if you're adding your own resource, you add any metadata that helper functions rely on.
        size: { w: 1920, h: 1080 }
      },
      automation: [
        { t: 0, x: 960, y: 540, w: 1920, h: 1080, r: 0 },
        { t, x: 960, y: 540, w: 1920, h: 1080, r: 0 }
      ]
    }

    // Finally, your function should call 'this.buildResources', which will automatically ensure that all the automations are specified correctly, create additional media resources records by looping to fulfill the demands specified by the automations, or omit invalid resources (i.e. ones with no automation)
    return this.buildResources();
}

module.exports = Builder;
</pre>

  </v-container>
</template>


<style scoped>
pre.code {
  height: 100%;
  white-space: pre-wrap;
  font-size: 14px;
  background-color: #FBE5E1;
  color: #C0341D;
  padding: 0 0.4rem;
}
</style>


<script>
import FaqHeader from '@/components/FaqHeader.vue';
import InputSectionTabs from '@/components/InputSectionTabs.vue';
import { getBackground } from '@/utils/group-video-template.js';
import { colors, fonts } from '@/card-customization-options.js';
import { mdiFileUpload, mdiVideoVintage, mdiFolderMultipleImage, mdiImport, mdiExport, mdiInformation, mdiCodeJson, mdiOpenInNew } from '@mdi/js';

export default {
  name: 'video-template-docs',

  components: {
    FaqHeader,
    InputSectionTabs
  },

  data() {
    return {
      tab_AUDIO_CHOICE: 0,
      tab_IMG: 0,
      tab_IMG_GROUP: 0,
      tab_MULTIPLE_CHOICE: 0,
      tab_TEXT: 0,
      tab_VIDEO: 0,

      selectedExampleTemplateId: null,
      exampleTemplates: [],
      loadExamplesStatus: this.$track('loadExamples'),
      getBackground
    };
  },

  created() {
    Object.assign(this, { colors, fonts, mdiFileUpload, mdiVideoVintage, mdiFolderMultipleImage, mdiImport, mdiExport, mdiFolderMultipleImage, mdiInformation, mdiCodeJson, mdiOpenInNew });
    this.loadExamples();
  },

  methods: {
    async loadExamples() {
      const response = await this.$call('list-group-video-templates');
      this.exampleTemplates = response.map(x => ({ text: x.name, value: x.template_id }));
      this.exampleTemplates.sort((a, b) => a.text.localeCompare(b.text));
      this.selectedExampleTemplateId = this.exampleTemplates[0].value;
    }
  }
}
</script>
